import React, { useCallback, useState } from 'react'
import moment from 'moment'
import styled from 'styled-components'
import { DayPickerRangeController, FocusedInputShape } from 'react-dates'
// @ts-ignore -- no typings for react-dates/constants
import { START_DATE, END_DATE } from 'react-dates/constants'

import { media } from 'styles/media'
import { useMediaQueryContext } from 'contexts/MediaQueryContext'

import DialogModal from 'components/display/DialogModal'
import InvisibleButton from 'components/display/InvisibleButton'
import AvailableWeekends from 'search/filters/common/AvailableWeekends'
import {
  AdultQuantitySelect,
  ChildQuantitySelect,
} from 'search/filters/common/QuantitySelect'
import { Filters } from '../types'
import { CallToActionButton } from '../../shared'

const Heading = styled.h2`
  font-size: ${({ theme }) => theme['-ds-font-size-xl']['font-size'].value};
  font-weight: 500;
  margin-bottom: 32px;
`

const SubHeading = styled.h3`
  font-size: ${({ theme }) => theme['-ds-font-size-md']['font-size'].value};
  font-weight: 400;
  margin-bottom: 24px;
`

const FilterWrapper = styled.div`
  padding: 24px 16px 90px 16px;

  ${media.md`
    padding: 39px 24px 0 24px;
  `}

  .DayPicker {
    margin: 0 auto;

    ${media.md`
      margin: 0 -20px 0;
    `}
  }
`

const PeopleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
  margin-top: 16px;
  padding-top: 30px;
  padding-bottom: 40px;

  ${SubHeading} {
    width: 100%;
  }

  > div:not(:last-child) {
    padding-bottom: 24px;
  }

  ${media.md`
    flex-wrap: wrap;
    flex-direction: row;
  `}
`

const AdultsWrapper = styled.div`
  ${media.md`
  padding-right: 70px;
  width: 51%;
  `}
`

const ChildrenWrapper = styled.div`
  ${media.md`
  padding-right: 70px;
  width: 49%;
  `}
`

const ButtonWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  border-top: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};
  padding: 12px 24px;
  background: ${({ theme }) => theme?.['-ds-neutral-100']?.value};

  ${media.sm`
    position: sticky;
    margin: 0 -24px;
    padding: 16px 24px;
  `}

  ${CallToActionButton} {
    max-width: 100%;
    width: 100%;
  }
`

const ViewTabs = styled.div`
  margin: 0 -16px;
  padding: 0 16px;

  border-bottom: 1px solid ${({ theme }) => theme?.['-ds-neutral-300']?.value};

  margin-bottom: 24px;

  & > *:not(:last-child) {
    margin-right: 24px;
  }
`

const Tab = styled(InvisibleButton)<{ $active: boolean }>`
  position: relative;
  padding: 12px 0;
  font-weight: 400;
  color: ${({ theme, $active }) =>
    $active
      ? theme?.['-ds-core-400']?.value
      : theme?.['-ds-neutral-700']?.value};

  font-size: ${({ theme }) =>
    theme?.['-ds-font-size-xs']?.['font-size']?.value};

  ${({ $active, theme }) =>
    $active &&
    `
    border-bottom: 2px solid ${theme?.['-ds-core-400']?.value};
  `}
`

type DateSearchType = 'specific-dates' | 'available-weekends'

interface FilterModalProps {
  open?: boolean
  filters: Omit<Filters, 'state' | 'country'>
  onDatesChange: ({
    startDate,
    endDate,
  }: Pick<Filters, 'startDate' | 'endDate'>) => any
  onWeekendMonthsChange: (_: string[]) => any
  onFilterChange: (type: string, value: string | number) => any
  onClose: () => any
  onSubmit: () => any
}

const FilterModal = ({
  open,
  filters,
  onDatesChange,
  onWeekendMonthsChange,
  onFilterChange,
  onClose,
  onSubmit,
}: FilterModalProps) => {
  const { md: largerThanMd } = useMediaQueryContext()
  const isMobile = !largerThanMd
  const [dateSearchType, setDateSearchType] = useState<DateSearchType>(
    'specific-dates'
  )
  const [focusedInput, setFocusedInput] = useState<FocusedInputShape>(
    START_DATE
  )
  const handleFocusChange = useCallback(
    (newFocusedInput: FocusedInputShape) => {
      setFocusedInput(newFocusedInput ? END_DATE : START_DATE)
    },
    []
  )

  return (
    <DialogModal
      isOpen={open}
      onRequestClose={onClose}
      smallModal
      closeButtonColour="-ds-neutral-700"
      rounded
      noPadding
    >
      <FilterWrapper>
        <Heading>Plan your escape</Heading>
        <ViewTabs>
          <Tab
            $active={dateSearchType === 'specific-dates'}
            onClick={() => setDateSearchType('specific-dates')}
          >
            Specific dates
          </Tab>
          <Tab
            $active={dateSearchType === 'available-weekends'}
            onClick={() => setDateSearchType('available-weekends')}
          >
            Available weekends
          </Tab>
        </ViewTabs>

        {dateSearchType === 'specific-dates' && (
          <DayPickerRangeController
            initialVisibleMonth={null}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onDatesChange={onDatesChange}
            focusedInput={focusedInput}
            onFocusChange={handleFocusChange}
            firstDayOfWeek={1}
            numberOfMonths={isMobile ? 1 : 2}
            isOutsideRange={(day) => day.isBefore(moment().startOf('day'))}
            transitionDuration={0} // Fix for iOS 14
            // @ts-ignore
            minDate={moment()}
            hideKeyboardShortcutsPanel
            noBorder
          />
        )}

        {dateSearchType === 'available-weekends' && (
          <AvailableWeekends
            selectedValues={filters.weekendMonths || []}
            onChange={({ value, checked }) => {
              if (checked) {
                onWeekendMonthsChange([...(filters.weekendMonths || []), value])
              } else {
                onWeekendMonthsChange(
                  (filters.weekendMonths || []).filter((v) => v !== value)
                )
              }
            }}
          />
        )}

        <PeopleWrapper>
          <SubHeading>Add people</SubHeading>
          <AdultsWrapper>
            <AdultQuantitySelect
              value={filters.adults}
              onChange={(count) => onFilterChange('adults', count)}
            />
          </AdultsWrapper>
          <ChildrenWrapper>
            <ChildQuantitySelect
              value={filters.children}
              onChange={(count) => onFilterChange('children', count)}
            />
          </ChildrenWrapper>
        </PeopleWrapper>

        <ButtonWrapper>
          <CallToActionButton type="button" onClick={onSubmit} rounded>
            Search escapes
          </CallToActionButton>
        </ButtonWrapper>
      </FilterWrapper>
    </DialogModal>
  )
}

export default FilterModal
